// Fichier: src/pages/Goals.js
import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { 
  collection, 
  getDocs, 
  deleteDoc, 
  doc,
  query,
  where
} from 'firebase/firestore';
import { 
  Box, 
  Typography, 
  Button, 
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  CircularProgress
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import EventIcon from '@mui/icons-material/Event';
import moment from 'moment';
import 'moment/locale/fr';

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [goalToDelete, setGoalToDelete] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Charger les objectifs
        const goalsSnapshot = await getDocs(collection(db, 'objectives'));
        const goalsData = goalsSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          deadline: doc.data().deadline ? doc.data().deadline.toDate() : null
        }));
        setGoals(goalsData);

        // Charger les élèves depuis users
        const usersSnapshot = await getDocs(collection(db, 'users'));
        const allUsers = usersSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          fullName: `${doc.data().firstName || ''} ${doc.data().lastName || ''}`.trim() || doc.data().email || doc.id
        }));
        setStudents(allUsers);

        // Charger les cours depuis dance_courses
        const coursesSnapshot = await getDocs(collection(db, 'dance_courses'));
        const coursesData = coursesSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(),
          title: doc.data().title || 'Cours sans titre'
        }));
        setCourses(coursesData);
      } catch (error) {
        console.error("Erreur de chargement des données:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDeleteGoal = async () => {
    if (!goalToDelete) return;
    
    try {
      await deleteDoc(doc(db, 'objectives', goalToDelete.id));
      setGoals(prev => prev.filter(g => g.id !== goalToDelete.id));
      setDeleteDialogOpen(false);
    } catch (error) {
      console.error("Erreur suppression :", error);
    }
  };

  const getAssignedNames = (ids, type) => {
    if (!ids || ids.length === 0) return [];
    
    const data = type === 'student' ? students : courses;
    return ids.map(id => {
      const item = data.find(i => i.id === id);
      return item ? (type === 'student' ? item.fullName : item.title) : 'Inconnu';
    });
  };

  const formatDeadline = (date) => {
    if (!date) return 'Aucune';
    return moment(date).format('DD/MM/YYYY [à] HH:mm');
  };

  const getDeadlineStatus = (date) => {
    if (!date) return 'none';
    const now = new Date();
    const deadlineDate = new Date(date);
    
    // Moins de 24h restantes
    if (deadlineDate - now < 86400000 && deadlineDate > now) return 'urgent';
    // Deadline passée
    if (deadlineDate < now) return 'passed';
    return 'normal';
  };

  const columns = [
    { 
      field: 'title', 
      headerName: 'Titre', 
      width: 200,
      renderCell: (params) => (
        <Button 
          onClick={() => navigate(`/goals/${params.row.id}`)}
          sx={{ 
            textTransform: 'none',
            color: 'primary.main',
            textAlign: 'left',
            justifyContent: 'flex-start',
            width: '100%'
          }}
        >
          {params.value}
        </Button>
      )
    },
    { field: 'description', headerName: 'Description', width: 300 },
    { 
      field: 'type', 
      headerName: 'Type', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={
            params.value === 'daily' ? 'Journalier' : 
            params.value === 'weekly' ? 'Hebdomadaire' : 
            params.value === 'monthly' ? 'Mensuel' : 
            'Annuel'
          } 
          color="primary"
          variant="outlined"
        />
      )
    },
    { 
      field: 'assignmentType', 
      headerName: 'Assignation', 
      width: 150,
      renderCell: (params) => (
        <Chip 
          label={params.value === 'course' ? 'Cours' : 'Élève'} 
          color="secondary"
          variant="outlined"
        />
      )
    },
    { 
      field: 'assignedTo', 
      headerName: 'Assigné à', 
      width: 300,
      renderCell: (params) => {
        const names = getAssignedNames(params.value, params.row.assignmentType);
        return (
          <Box>
            {names.slice(0, 3).map((name, index) => (
              <Chip 
                key={index} 
                label={name} 
                size="small" 
                sx={{ mr: 0.5, mb: 0.5 }} 
              />
            ))}
            {names.length > 3 && (
              <Chip 
                label={`+${names.length - 3} autres`} 
                size="small" 
                variant="outlined"
              />
            )}
          </Box>
        );
      }
    },
    { 
      field: 'deadline', 
      headerName: 'Deadline', 
      width: 200,
      renderCell: (params) => {
        const status = getDeadlineStatus(params.value);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EventIcon 
              fontSize="small" 
              color={
                status === 'passed' ? 'error' : 
                status === 'urgent' ? 'warning' : 'disabled'
              } 
            />
            <Typography 
              variant="body2" 
              color={
                status === 'passed' ? 'error' : 
                status === 'urgent' ? 'warning.main' : 'text.secondary'
              }
            >
              {formatDeadline(params.value)}
            </Typography>
          </Box>
        );
      }
    },
    { 
      field: 'progress', 
      headerName: 'Progrès', 
      width: 150,
      renderCell: (params) => {
        const progress = typeof params.value === 'number' ? params.value : 0;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1 }}>
              <Box 
                sx={{ 
                  height: 8, 
                  width: `${progress}%`, 
                  bgcolor: progress === 100 ? 'success.main' : 'primary.main',
                  borderRadius: 1
                }} 
              />
            </Box>
            <Typography variant="body2">{progress}%</Typography>
          </Box>
        );
      }
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 200,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => navigate(`/goals/edit/${params.row.id}`)}
          >
            Modifier
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            onClick={() => {
              setGoalToDelete(params.row);
              setDeleteDialogOpen(true);
            }}
          >
            Supprimer
          </Button>
        </Box>
      )
    }
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={60} sx={{ color: '#7C3AED' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3 
      }}>
        <Typography variant="h4">Gestion des Objectifs</Typography>
        <Button
          variant="contained"
          component={Link}
          to="/goals/new"
          sx={{ backgroundColor: '#7C3AED', '&:hover': { backgroundColor: '#6D28D9' } }}
        >
          Créer un Objectif
        </Button>
      </Box>

      <Box sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={goals}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
          sx={{
            '& .MuiDataGrid-cell:hover': {
              backgroundColor: 'rgba(124, 58, 237, 0.04)'
            }
          }}
        />
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirmer la suppression</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Êtes-vous sûr de vouloir supprimer l'objectif "{goalToDelete?.title}" ? 
            Cette action est irréversible.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Annuler</Button>
          <Button onClick={handleDeleteGoal} color="error" autoFocus>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Goals;